/**
 * Peças comuns às telas que leem GET /api/sales-report/station/:id — a lista da
 * tela Vendas e o Relatório de Vendas. As duas mostram as mesmas vendas (links,
 * pedidos de produto e assinaturas) com os mesmos rótulos.
 */
import axios from 'axios';
import { endOfMonth, format, startOfMonth, startOfYear, subDays, subMonths } from 'date-fns';
import { BASE_API, USER_TOKEN } from 'config/consts';

export const SOURCE_CONFIG = {
  link: { label: 'Link', bg: '#eef2ff', fg: '#4338ca' },
  produto: { label: 'Produto', bg: '#e6fbf5', fg: '#0f766e' },
  assinatura: { label: 'Assinatura', bg: '#f3e8ff', fg: '#7e22ce' },
};

export const RECURRENCE_LABELS = {
  single: 'Venda única',
  weekly: 'Semanal',
  biweekly: 'Quinzenal',
  monthly: 'Mensal',
  bimonthly: 'Bimestral',
  quarterly: 'Trimestral',
  semiannual: 'Semestral',
  annual: 'Anual',
};

const STATUS_ENUM_LABEL = {
  paid: 'Pago',
  pending: 'Pendente',
  waiting_payment: 'Aguardando',
  processing: 'Processando',
  expired: 'Expirado',
  canceled: 'Cancelado',
  refunded: 'Estornado',
  refused: 'Recusado',
  unpaid: 'Inadimplente',
  // Estados que só a Core API v5 produz. Sem eles a tela mostraria o termo
  // cru em inglês para o gestor.
  failed: 'Recusado',
  partial_canceled: 'Estornado parcial',
  chargedback: 'Chargeback',
  overpaid: 'Pago a maior',
  underpaid: 'Pago a menor',
};

const NEGATIVE_STATUSES = new Set([
  'canceled',
  'refused',
  'expired',
  'unpaid',
  'refunded',
  'failed',
  'partial_canceled',
  'chargedback',
  'underpaid',
  'Cancelado',
  'Expirado',
  'Estornada',
  'Parcialmente estornada',
  'Pagamento recusado',
]);

export const formatCurrency = (value) =>
  Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('pt-BR');
};

export const mapPaymentMethod = (method) => {
  if (!method) return '—';
  const n = method.toLowerCase();
  if (n === 'pix') return 'PIX';
  if (n.includes('credit') || n.includes('crédito') || n === 'cartao') return 'Cartão de crédito';
  if (n === 'boleto') return 'Boleto';
  return method;
};

export const statusLabel = (row) => STATUS_ENUM_LABEL[row.status] || row.status || '—';

export const statusStyle = (row) => {
  if (Number(row.is_paid)) return { backgroundColor: '#e6f4ea', color: '#1b7a3d' };
  if (NEGATIVE_STATUSES.has(row.status)) return { backgroundColor: '#fdecea', color: '#b3261e' };
  return { backgroundColor: '#fff8e1', color: '#8d6e00' };
};

export const recurrenceLabel = (row) =>
  Number(row.is_recurring)
    ? RECURRENCE_LABELS[row.recurrence] || row.recurrence || 'Recorrente'
    : '';

/** Teto de linhas numa exportação: o mesmo do servidor. */
export const LIMITE_DE_EXPORTACAO = 10000;

/**
 * Parâmetros da consulta a partir dos filtros da tela. Datas vazias ficam de
 * fora: sem elas o servidor mostra o mês corrente, ou o histórico inteiro
 * quando há busca ou `allHistory`.
 */
export const paramsDosFiltros = ({
  startDate,
  endDate,
  allHistory,
  source,
  paidOnly,
  search,
  page = 0,
  pageSize = 25,
}) => {
  const params = {
    source: source || 'all',
    paidOnly: paidOnly ? 'true' : 'false',
    page,
    pageSize,
  };
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (allHistory && !startDate && !endDate) params.period = 'all';
  if (search) params.search = search;
  return params;
};

export const buscarVendas = async (stationId, params) => {
  const token = JSON.parse(localStorage.getItem(USER_TOKEN));
  const response = await axios.get(`${BASE_API}/sales-report/station/${stationId}`, {
    params,
    headers: { Authorization: `${token}` },
  });
  return response.data;
};

const BRASILIA_MS = 3 * 60 * 60 * 1000;
const emDiaDeBrasilia = (ms) => new Date(ms - BRASILIA_MS).toISOString().slice(0, 10);

/**
 * Período efetivo devolvido pelo servidor ([start, end) em UTC) como datas
 * AAAA-MM-DD de Brasília, com o último dia incluído.
 */
export const datasDoPeriodo = (period) => {
  if (!period || !period.start || !period.end) return null;
  return {
    start: emDiaDeBrasilia(new Date(period.start).getTime()),
    end: emDiaDeBrasilia(new Date(period.end).getTime() - 1),
  };
};

const paraBr = (iso) => iso.split('-').reverse().join('/');

/** Texto do período em vigor, para o gestor saber o que a lista está mostrando. */
export const textoDoPeriodo = (period) => {
  if (!period) return '';
  if (period.allHistory) return 'Todo o histórico';
  const datas = datasDoPeriodo(period);
  if (!datas) return '';
  return datas.start === datas.end
    ? paraBr(datas.start)
    : `${paraBr(datas.start)} a ${paraBr(datas.end)}`;
};

const dia = (d) => format(d, 'yyyy-MM-dd');

/**
 * Atalhos de período. `all` limpa as datas e pede o histórico inteiro. "Este
 * mês" grava as datas: sem datas a tela também mostra o mês corrente, mas aí a
 * busca procura no histórico inteiro — com o atalho, o gestor escolheu o mês.
 */
export const ATALHOS_DE_PERIODO = [
  {
    key: 'month',
    label: 'Este mês',
    range: () => ({
      startDate: dia(startOfMonth(new Date())),
      endDate: dia(endOfMonth(new Date())),
    }),
  },
  {
    key: 'lastMonth',
    label: 'Mês passado',
    range: () => {
      const ref = subMonths(new Date(), 1);
      return { startDate: dia(startOfMonth(ref)), endDate: dia(endOfMonth(ref)) };
    },
  },
  {
    key: '90d',
    label: 'Últimos 90 dias',
    range: () => ({ startDate: dia(subDays(new Date(), 89)), endDate: dia(new Date()) }),
  },
  {
    key: 'year',
    label: 'Este ano',
    range: () => ({ startDate: dia(startOfYear(new Date())), endDate: dia(new Date()) }),
  },
  { key: 'all', label: 'Todo o histórico', range: () => ({ startDate: '', endDate: '' }) },
];
